import React, { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../i18n';
import { useNavigate } from 'react-router-dom';
import useSubscription from '../../hooks/useSubscription';
import UpgradeOverlay from '../../components/UpgradeOverlay';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Search as SearchIcon,
  Security as SecurityIcon,
  Email as EmailIcon,
  Public as PublicIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  Shield as ShieldIcon,
  BugReport as BugReportIcon,
  Language as LanguageIcon,
  Dns as DnsIcon,
  Storage as StorageIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import apiClient, { auth } from '../../services/api/client';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';

// Score indicator component
const ScoreIndicator = ({ score, maxScore = 100, size = 'medium' }) => {
  const percentage = (score / maxScore) * 100;
  let color = 'success';
  if (percentage < 40) color = 'error';
  else if (percentage < 70) color = 'warning';

  const sizeMap = {
    small: { width: 40, height: 40, fontSize: '0.75rem' },
    medium: { width: 60, height: 60, fontSize: '1rem' },
    large: { width: 80, height: 80, fontSize: '1.25rem' },
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', ...sizeMap[size] }}>
      <CircularProgress
        variant="determinate"
        value={percentage}
        color={color}
        size={sizeMap[size].width}
        thickness={4}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: sizeMap[size].fontSize }}>
          {score}
        </Typography>
      </Box>
    </Box>
  );
};

// Threat level badge
const ThreatBadge = ({ level, t }) => {
  const colorMap = {
    critical: 'error',
    high: 'error',
    medium: 'warning',
    low: 'info',
    safe: 'success',
  };

  const levelKey = level?.toLowerCase();
  const translatedLevel = t(`enrichment.threatLevels.${levelKey}`, level)?.toUpperCase() || t('common.unknown', 'UNKNOWN');

  return (
    <Chip
      label={translatedLevel}
      color={colorMap[levelKey] || 'default'}
      size="small"
      icon={level === 'safe' ? <CheckCircleIcon /> : <WarningIcon />}
    />
  );
};

// Main component
const EnrichmentDashboard = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { canUseEnrichment, loading: subscriptionLoading } = useSubscription();

  const translateScanType = (scanType) => {
    if (!scanType) return '-';
    // Check enrichment-specific scan types first
    const enrichmentKey = `enrichment.scanTypes.${scanType}`;
    const enrichmentTranslated = t(enrichmentKey);
    if (enrichmentTranslated !== enrichmentKey) return enrichmentTranslated;
    // Check generic scans.types namespace
    const key = `scans.types.${scanType}`;
    const translated = t(key);
    if (translated !== key) return translated;
    // Check OSINT namespace
    const osintKey = `osint.scanTypes.${scanType}`;
    const osintTranslated = t(osintKey);
    if (osintTranslated !== osintKey) return osintTranslated;
    // Fallback: format the scan type for display (capitalized with spaces)
    return scanType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // State
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // Track if we're in an auth redirect to prevent showing errors during navigation
  const [isAuthRedirecting, setIsAuthRedirecting] = useState(false);

  // Scan form state
  const [scanTarget, setScanTarget] = useState('');
  const [scanType, setScanType] = useState('domain');
  const [selectedModules, setSelectedModules] = useState(['virustotal', 'security_headers', 'email_security', 'geoip']);

  // Results state
  const [scans, setScans] = useState([]);
  const [currentScan, setCurrentScan] = useState(null);
  const [threatSummary, setThreatSummary] = useState(null);

  // Quick scan results
  const [quickResult, setQuickResult] = useState(null);

  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedScan, setSelectedScan] = useState(null);

  // Check if user has a valid token before making API calls
  // Calls auth utility directly (no memoization) to ensure fresh check each time
  // Returns true only if both token exists AND is not expired
  const hasValidToken = () => auth.isAuthenticated() && auth.isTokenValidClientSide();

  // Helper to check if error is authentication-related
  const isAuthError = useCallback((err) => {
    // Check multiple indicators that this is an auth error
    const status = err.response?.status;
    const message = err.message?.toLowerCase() || '';
    const detail = err.response?.data?.detail?.toLowerCase() || '';
    const statusText = err.response?.statusText?.toLowerCase() || '';

    const isAuth = err.isAuthRedirect ||
           status === 401 ||
           status === 403 ||
           message.includes('401') ||
           message.includes('403') ||
           message.includes('credentials') ||
           message.includes('authentication') ||
           message.includes('unauthorized') ||
           message.includes('unauthenticated') ||
           message.includes('not authenticated') ||
           detail.includes('credentials') ||
           detail.includes('unauthorized') ||
           detail.includes('token') ||
           detail.includes('not authenticated') ||
           statusText.includes('unauthorized');

    // If it's an auth error, mark that we're redirecting to prevent UI updates
    if (isAuth) {
      setIsAuthRedirecting(true);
    }

    return isAuth;
  }, []);

  // Helper to check if a message contains auth-related content
  const isAuthRelatedMessage = useCallback((msg) => {
    if (!msg) return false;
    const lowerMsg = msg.toLowerCase();
    // Comprehensive list of auth/error patterns that should not be shown to users
    const sensitivePatterns = [
      'credential', 'token', '401', '403', '402', '500', '502', '503', '504',
      'unauthorized', 'unauthenticated', 'authentication', 'forbidden', 'jwt',
      'bearer', 'session', 'login required', 'not authenticated',
      'api error', 'network error', 'request failed', 'validate',
      'status code', 'http error', 'axios error', 'could not validate',
      'invalid', 'expired', 'insufficient', 'required:', 'failed to'
    ];
    return sensitivePatterns.some(pattern => lowerMsg.includes(pattern));
  }, []);

  // Helper to sanitize error messages - never show auth-related error details
  const getSafeErrorMessage = useCallback((err, defaultMessage) => {
    // Always return default for auth-related HTTP status codes
    const status = err.response?.status;
    if (status === 401 || status === 403 || status === 402) {
      return defaultMessage;
    }

    const errorDetail = err.response?.data?.detail || err.message || '';

    // If error detail contains any sensitive info, return the default message
    if (isAuthRelatedMessage(errorDetail) || !errorDetail) {
      return defaultMessage;
    }

    return errorDetail;
  }, [isAuthRelatedMessage]);

  // Fetch scans list
  const fetchScans = useCallback(async () => {
    // Skip API call if no token - prevents 401 errors during auth transitions
    if (!hasValidToken()) return;
    try {
      const response = await apiClient.get('/enrichment/scans');
      setScans(response.data.items || []);
    } catch (err) {
      // Silently handle auth errors - user will be redirected to login by API client
      if (isAuthError(err)) {
        setError(null); // Clear any previous error to avoid stale UI
        return;
      }
      // Non-auth errors are silently ignored - user will see empty list
    }
  }, [isAuthError]);

  // Fetch threat summary
  const fetchThreatSummary = useCallback(async () => {
    // Skip API call if no token - prevents 401 errors during auth transitions
    if (!hasValidToken()) return;
    try {
      const response = await apiClient.get('/enrichment/threat-intelligence/summary');
      setThreatSummary(response.data);
    } catch (err) {
      // Silently handle auth errors - user will be redirected to login by API client
      if (isAuthError(err)) {
        setError(null); // Clear any previous error to avoid stale UI
        return;
      }
      // Non-auth errors are silently ignored - user will see empty summary
    }
  }, [isAuthError]);

  // Clear error when auth redirect is detected - ensures no error message leaks through
  useEffect(() => {
    if (isAuthRedirecting) {
      setError(null);
      setSuccess(null);
    }
  }, [isAuthRedirecting]);

  // Initial load - only fetch if we have a valid token
  useEffect(() => {
    let isMounted = true;

    // Clear any stale errors on mount
    setError(null);
    setIsAuthRedirecting(false);

    // Double-check authentication before making any API calls
    // This provides an additional safety layer even though ProtectedRoute should handle this
    if (!auth.isAuthenticated() || !auth.isTokenValidClientSide()) {
      // No token or expired token - set redirecting state to suppress any error display
      setIsAuthRedirecting(true);
      return;
    }

    // Wrap in async IIFE to handle any unexpected errors
    (async () => {
      try {
        await Promise.all([fetchScans(), fetchThreatSummary()]);
      } catch (err) {
        // Silently ignore all errors during initial load
        // Auth errors are handled by fetchScans/fetchThreatSummary
        // and the API interceptor will redirect if needed
        if (isMounted) {
          console.debug('[Enrichment] Initial load error (ignored):', err.message);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [fetchScans, fetchThreatSummary]);

  // Start enrichment scan
  const handleStartScan = async () => {
    if (!scanTarget.trim()) {
      setError(t('enrichment.error.targetRequired', 'La cible est requise'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        target: scanTarget.trim(),
        scan_type: scanType,
        modules: selectedModules,
      };

      const response = await apiClient.post('/enrichment/scans', payload);
      setSuccess(t('enrichment.success.scanStarted', 'Scan started successfully'));
      setCurrentScan(response.data);
      fetchScans();

      // Clear form
      setScanTarget('');
    } catch (err) {
      // Silently handle auth errors - user will be redirected to login by API client
      if (isAuthError(err)) return;
      // Ensure error message doesn't contain auth-related info
      setError(getSafeErrorMessage(err, t('enrichment.error.scanFailed', 'Scan start failed')));
    } finally {
      setLoading(false);
    }
  };

  // Quick scan handlers
  const handleQuickScan = async (type, target) => {
    if (!target?.trim()) {
      setError(t('enrichment.error.targetRequired', 'La cible est requise'));
      return;
    }

    setLoading(true);
    setError(null);
    setQuickResult(null);

    try {
      let endpoint = '';
      switch (type) {
        case 'virustotal':
          endpoint = `/enrichment/quick/virustotal?domain=${encodeURIComponent(target)}`;
          break;
        case 'headers':
          endpoint = `/enrichment/quick/headers?url=${encodeURIComponent(target.startsWith('http') ? target : `https://${target}`)}`;
          break;
        case 'email':
          endpoint = `/enrichment/quick/email-security?domain=${encodeURIComponent(target)}`;
          break;
        case 'geoip':
          endpoint = `/enrichment/quick/geoip?ip=${encodeURIComponent(target)}`;
          break;
        case 'reverse_ip':
          endpoint = `/enrichment/quick/reverse-ip?ip=${encodeURIComponent(target)}`;
          break;
        default:
          throw new Error('Unknown scan type');
      }

      const response = await apiClient.get(endpoint);
      setQuickResult({ type, data: response.data });
      setSuccess(t('enrichment.success.quickScanComplete', 'Quick scan completed'));
    } catch (err) {
      // Silently handle auth errors - user will be redirected to login by API client
      if (isAuthError(err)) return;
      // Ensure error message doesn't contain auth-related info
      setError(getSafeErrorMessage(err, t('enrichment.error.quickScanFailed', 'Quick scan failed')));
    } finally {
      setLoading(false);
    }
  };

  // View scan details
  const handleViewScan = async (scanId) => {
    try {
      const response = await apiClient.get(`/enrichment/scans/${scanId}`);
      setSelectedScan(response.data);
      setDetailDialogOpen(true);
    } catch (err) {
      // Silently handle auth errors - user will be redirected to login by API client
      if (isAuthError(err)) {
        setError(null);
        return;
      }
      setError(t('enrichment.error.loadFailed', 'Failed to load scan details'));
    }
  };

  // Delete scan
  const handleDeleteScan = async (scanId) => {
    try {
      await apiClient.delete(`/enrichment/scans/${scanId}`);
      setSuccess(t('enrichment.success.scanDeleted', 'Scan deleted'));
      fetchScans();
    } catch (err) {
      // Silently handle auth errors - user will be redirected to login by API client
      if (isAuthError(err)) {
        setError(null);
        return;
      }
      setError(t('enrichment.error.deleteFailed', 'Scan deletion failed'));
    }
  };

  // Render scan form
  const renderScanForm = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('enrichment.newScan', 'Nouveau scan d\'enrichissement')}
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label={t('enrichment.target', 'Cible')}
              value={scanTarget}
              onChange={(e) => setScanTarget(e.target.value)}
              placeholder="exemple.com, 1.2.3.4, https://..."
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>{t('enrichment.type', 'Type')}</InputLabel>
              <Select
                value={scanType}
                onChange={(e) => setScanType(e.target.value)}
                label={t('enrichment.type', 'Type')}
                disabled={loading}
              >
                <MenuItem value="domain">{t('enrichment.scanTypes.domain', 'Domaine')}</MenuItem>
                <MenuItem value="ip">{t('enrichment.scanTypes.ip', 'Adresse IP')}</MenuItem>
                <MenuItem value="url">{t('enrichment.scanTypes.url', 'URL')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('enrichment.modules', 'Modules')}</InputLabel>
              <Select
                multiple
                value={selectedModules}
                onChange={(e) => setSelectedModules(e.target.value)}
                label={t('enrichment.modules', 'Modules')}
                disabled={loading}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={t(`enrichment.scanTypes.${value}`, value)} size="small" />
                    ))}
                  </Box>
                )}
              >
                <MenuItem value="virustotal">{t('enrichment.scanTypes.virustotal', 'VirusTotal')}</MenuItem>
                <MenuItem value="security_headers">{t('enrichment.scanTypes.security_headers', 'Security headers')}</MenuItem>
                <MenuItem value="email_security">{t('enrichment.scanTypes.email_security', 'Email security')}</MenuItem>
                <MenuItem value="geoip">{t('enrichment.scanTypes.geoip', 'IP geolocation')}</MenuItem>
                <MenuItem value="reverse_ip">{t('enrichment.scanTypes.reverse_ip', 'Reverse IP')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleStartScan}
              disabled={loading || !scanTarget.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ textTransform: 'none' }}
            >
              {t('enrichment.scan', 'Analyser')}
            </Button>
          </Grid>
        </Grid>

        {/* Quick scan buttons */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t('enrichment.quickScans', 'Scans rapides')}\u202F:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ShieldIcon />}
              onClick={() => handleQuickScan('virustotal', scanTarget)}
              disabled={!scanTarget.trim() || loading}
              sx={{ textTransform: 'none' }}
            >
              {t('enrichment.scanTypes.virustotal', 'VirusTotal')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<SecurityIcon />}
              onClick={() => handleQuickScan('headers', scanTarget)}
              disabled={!scanTarget.trim() || loading}
              sx={{ textTransform: 'none' }}
            >
              {t('enrichment.scanTypes.headers', 'Security headers')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={() => handleQuickScan('email', scanTarget)}
              disabled={!scanTarget.trim() || loading}
              sx={{ textTransform: 'none' }}
            >
              {t('enrichment.scanTypes.email_security', 'Email security')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<PublicIcon />}
              onClick={() => handleQuickScan('geoip', scanTarget)}
              disabled={!scanTarget.trim() || loading}
              sx={{ textTransform: 'none' }}
            >
              {t('enrichment.scanTypes.geoip', 'IP geolocation')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DnsIcon />}
              onClick={() => handleQuickScan('reverse_ip', scanTarget)}
              disabled={!scanTarget.trim() || loading}
              sx={{ textTransform: 'none' }}
            >
              {t('enrichment.scanTypes.reverse_ip', 'Reverse IP')}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Render quick result
  const renderQuickResult = () => {
    if (!quickResult) return null;

    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('enrichment.quickResult', 'Quick scan result')} - {quickResult.type}
          </Typography>

          {quickResult.type === 'virustotal' && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">{t('enrichment.virustotal.malicious', 'Malveillant')}</Typography>
                  <Typography variant="h4" color="error">{quickResult.data.stats?.malicious || 0}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">{t('enrichment.virustotal.suspicious', 'Suspect')}</Typography>
                  <Typography variant="h4" color="warning.main">{quickResult.data.stats?.suspicious || 0}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">{t('enrichment.virustotal.harmless', 'Inoffensif')}</Typography>
                  <Typography variant="h4" color="success.main">{quickResult.data.stats?.harmless || 0}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">{t('enrichment.riskScore', 'Score de risque')}</Typography>
                  <ScoreIndicator score={100 - (quickResult.data.threat_score || 0)} />
                </Grid>
              </Grid>
            </Box>
          )}

          {quickResult.type === 'headers' && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <ScoreIndicator score={quickResult.data.score || 0} size="large" />
                <Box>
                  <Typography variant="h4">{quickResult.data.grade || t('common.notAvailable', 'N/D')}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('enrichment.headers.grade', 'Security grade')}</Typography>
                </Box>
              </Box>
              {quickResult.data.headers && (
                <List dense>
                  {quickResult.data.headers.slice(0, 5).map((header, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon>
                        {header.present ? <CheckCircleIcon color="success" /> : <ErrorIcon color="error" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={header.header}
                        secondary={header.present ? header.value?.substring(0, 50) : t('enrichment.headers.missing', 'Manquant')}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          {quickResult.type === 'email' && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Chip
                    label={`SPF: ${quickResult.data.spf?.valid ? t('enrichment.email.valid', 'Valide') : t('enrichment.email.invalid', 'Invalide')}`}
                    color={quickResult.data.spf?.valid ? 'success' : 'error'}
                    icon={quickResult.data.spf?.valid ? <CheckCircleIcon /> : <ErrorIcon />}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Chip
                    label={`DKIM: ${quickResult.data.dkim?.valid ? t('enrichment.email.valid', 'Valide') : t('enrichment.email.invalid', 'Invalide')}`}
                    color={quickResult.data.dkim?.valid ? 'success' : 'error'}
                    icon={quickResult.data.dkim?.valid ? <CheckCircleIcon /> : <ErrorIcon />}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Chip
                    label={`DMARC: ${quickResult.data.dmarc?.valid ? t('enrichment.email.valid', 'Valide') : t('enrichment.email.invalid', 'Invalide')}`}
                    color={quickResult.data.dmarc?.valid ? 'success' : 'error'}
                    icon={quickResult.data.dmarc?.valid ? <CheckCircleIcon /> : <ErrorIcon />}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">{t('enrichment.results.riskScore', 'Score global')}</Typography>
                <ScoreIndicator score={quickResult.data.overall_score || 0} />
              </Box>
            </Box>
          )}

          {quickResult.type === 'geoip' && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">{t('enrichment.geoip.location', 'Localisation')}</Typography>
                  <Typography variant="body1">
                    {quickResult.data.city}, {quickResult.data.country_name} ({quickResult.data.country_code})
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">{t('enrichment.geoip.isp', 'Fournisseur (ISP)')}</Typography>
                  <Typography variant="body1">{quickResult.data.isp || t('common.unknown', 'Inconnu')}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {quickResult.data.is_proxy && <Chip label="Proxy" color="warning" size="small" />}
                    {quickResult.data.is_vpn && <Chip label="VPN" color="warning" size="small" />}
                    {quickResult.data.is_tor && <Chip label="Tor" color="error" size="small" />}
                    {quickResult.data.is_datacenter && <Chip label="Datacenter" color="info" size="small" />}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <ThreatBadge level={quickResult.data.threat_level} t={t} />
                </Grid>
              </Grid>
            </Box>
          )}

          {quickResult.type === 'reverse_ip' && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">{t('enrichment.reverseIp.totalDomains', 'Domains found')}</Typography>
                  <Typography variant="h4">{quickResult.data.total_domains || 0}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">{t('enrichment.reverseIp.suspiciousDomains', 'Domaines suspects')}</Typography>
                  <Typography variant="h4" color="warning.main">{quickResult.data.suspicious_count || 0}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">{t('enrichment.reverseIp.sharedHosting', 'Shared hosting')}</Typography>
                  <Chip
                    label={quickResult.data.shared_hosting ? t('common.yes', 'Oui') : t('common.no', 'Non')}
                    color={quickResult.data.shared_hosting ? 'warning' : 'success'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">{t('enrichment.reverseIp.riskScore', 'Score de risque')}</Typography>
                  <ScoreIndicator score={100 - (quickResult.data.risk_score || 0)} />
                </Grid>
              </Grid>
            </Box>
          )}

          <Button
            size="small"
            variant="outlined"
            onClick={() => setQuickResult(null)}
            sx={{ mt: 2, textTransform: 'none' }}
          >
            {t('common.close', 'Fermer')}
          </Button>
        </CardContent>
      </Card>
    );
  };

  // Render threat summary
  const renderThreatSummary = () => {
    if (!threatSummary) return null;

    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('enrichment.threatSummary', 'Threat summary')}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light', color: 'error.contrastText' }}>
                <Typography variant="h3">{threatSummary.critical_threats || 0}</Typography>
                <Typography variant="body2">{t('enrichment.threatLevels.critical', 'Critique')}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                <Typography variant="h3">{threatSummary.high_threats || 0}</Typography>
                <Typography variant="body2">{t('enrichment.threatLevels.high', 'High')}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                <Typography variant="h3">{threatSummary.medium_threats || 0}</Typography>
                <Typography variant="body2">{t('enrichment.threatLevels.medium', 'Moyenne')}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                <Typography variant="h3">{threatSummary.low_threats || 0}</Typography>
                <Typography variant="body2">{t('enrichment.threatLevels.low', 'Faible')}</Typography>
              </Paper>
            </Grid>
          </Grid>

          {threatSummary.recent_indicators && threatSummary.recent_indicators.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>{t('enrichment.recentIndicators', 'Recent indicators')}</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {threatSummary.recent_indicators.slice(0, 10).map((indicator, idx) => (
                  <Chip
                    key={idx}
                    label={indicator.value}
                    size="small"
                    color={indicator.threat_level === 'high' ? 'error' : 'default'}
                  />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // Render scans table
  const renderScansTable = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('enrichment.recentScans', 'Recent scans')}
          </Typography>
          <IconButton onClick={fetchScans} size="small">
            <RefreshIcon />
          </IconButton>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('enrichment.target', 'Cible')}</TableCell>
                <TableCell>{t('enrichment.type', 'Type')}</TableCell>
                <TableCell>{t('enrichment.statusLabel', 'Statut')}</TableCell>
                <TableCell>{t('enrichment.riskScore', 'Score de risque')}</TableCell>
                <TableCell>{t('enrichment.date', 'Date')}</TableCell>
                <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      {t('enrichment.noScans', 'Aucun scan pour le moment')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                scans.map((scan) => (
                  <TableRow key={scan.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {scan.target}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={translateScanType(scan.scan_type)} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={scan.status}
                        size="small"
                        color={
                          scan.status === 'completed' ? 'success' :
                          scan.status === 'failed' ? 'error' :
                          scan.status === 'running' ? 'primary' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {scan.risk_score !== null && (
                        <ScoreIndicator score={100 - scan.risk_score} size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(scan.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('common.view', 'Voir')}>
                        <IconButton size="small" onClick={() => handleViewScan(scan.id)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete', 'Supprimer')}>
                        <IconButton size="small" onClick={() => handleDeleteScan(scan.id)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );

  // Render detail dialog
  const renderDetailDialog = () => (
    <Dialog
      open={detailDialogOpen}
      onClose={() => setDetailDialogOpen(false)}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon />
          {t('enrichment.scanDetails', 'Scan details')}: {selectedScan?.target}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {selectedScan && (
          <Box>
            {/* VirusTotal Results */}
            {selectedScan.results?.virustotal && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <ShieldIcon sx={{ mr: 1 }} />
                  <Typography>VirusTotal</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography color="error">
                        {t('enrichment.virustotal.malicious', 'Malveillant')}: {selectedScan.results.virustotal.stats?.malicious || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography color="warning.main">
                        {t('enrichment.virustotal.suspicious', 'Suspect')}: {selectedScan.results.virustotal.stats?.suspicious || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography color="success.main">
                        {t('enrichment.virustotal.harmless', 'Inoffensif')}: {selectedScan.results.virustotal.stats?.harmless || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography>
                        {t('enrichment.virustotal.undetected', 'Undetected')}: {selectedScan.results.virustotal.stats?.undetected || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Security Headers */}
            {selectedScan.results?.security_headers && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <SecurityIcon sx={{ mr: 1 }} />
                  <Typography>{t('enrichment.headers.title', 'Security headers')} - {t('enrichment.headers.grade', 'Grade')}: {selectedScan.results.security_headers.grade}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {selectedScan.results.security_headers.headers?.map((header, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon>
                          {header.present ? <CheckCircleIcon color="success" /> : <ErrorIcon color="error" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={header.header}
                          secondary={header.present ? header.value : t('enrichment.headers.missing', 'Not configured')}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Email Security */}
            {selectedScan.results?.email_security && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <EmailIcon sx={{ mr: 1 }} />
                  <Typography>{t('enrichment.email.title', 'Email security')} - {t('enrichment.headers.score', 'Score')}: {selectedScan.results.email_security.overall_score}/100</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2">SPF</Typography>
                      <Chip
                        label={selectedScan.results.email_security.spf?.valid ? t('enrichment.email.valid', 'Valide') : t('enrichment.email.invalid', 'Invalide')}
                        color={selectedScan.results.email_security.spf?.valid ? 'success' : 'error'}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2">DKIM</Typography>
                      <Chip
                        label={selectedScan.results.email_security.dkim?.valid ? t('enrichment.email.valid', 'Valide') : t('enrichment.email.invalid', 'Invalide')}
                        color={selectedScan.results.email_security.dkim?.valid ? 'success' : 'error'}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="subtitle2">DMARC</Typography>
                      <Chip
                        label={selectedScan.results.email_security.dmarc?.valid ? t('enrichment.email.valid', 'Valide') : t('enrichment.email.invalid', 'Invalide')}
                        color={selectedScan.results.email_security.dmarc?.valid ? 'success' : 'error'}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                  {selectedScan.results.email_security.vulnerabilities?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="error">{t('enrichment.email.vulnerabilities', 'Detected vulnerabilities')}</Typography>
                      <List dense>
                        {selectedScan.results.email_security.vulnerabilities.map((vuln, idx) => (
                          <ListItem key={idx}>
                            <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                            <ListItemText primary={vuln} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {/* GeoIP */}
            {selectedScan.results?.geoip && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <PublicIcon sx={{ mr: 1 }} />
                  <Typography>GeoIP - {selectedScan.results.geoip.country_name}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">{t('enrichment.geoip.location', 'Localisation')}</Typography>
                      <Typography>
                        {selectedScan.results.geoip.city}, {selectedScan.results.geoip.region}
                      </Typography>
                      <Typography>
                        {selectedScan.results.geoip.country_name} ({selectedScan.results.geoip.country_code})
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2">{t('enrichment.geoip.network', 'Network information')}</Typography>
                      <Typography>{t('enrichment.geoip.isp', 'Fournisseur (ISP)')}: {selectedScan.results.geoip.isp}</Typography>
                      <Typography>AS: {selectedScan.results.geoip.as_name}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        {selectedScan.results.geoip.is_proxy && <Chip label="Proxy" color="warning" size="small" />}
                        {selectedScan.results.geoip.is_vpn && <Chip label="VPN" color="warning" size="small" />}
                        {selectedScan.results.geoip.is_tor && <Chip label="Tor" color="error" size="small" />}
                        {selectedScan.results.geoip.is_datacenter && <Chip label="Datacenter" color="info" size="small" />}
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            )}

            {/* AI Analysis */}
            {selectedScan.ai_analysis && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <SmartToyIcon sx={{ mr: 1 }} />
                  <Typography>{t('enrichment.aiAnalysis.title', 'Analyse IA')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="subtitle2">{t('enrichment.aiAnalysis.executiveSummary', 'Executive summary')}</Typography>
                  <Box sx={{ mb: 2 }}>
                    <MarkdownRenderer content={selectedScan.ai_analysis.executive_summary} variant="compact" />
                  </Box>

                  <Typography variant="subtitle2">{t('enrichment.aiAnalysis.keyFindings', 'Key findings')}</Typography>
                  <List dense>
                    {selectedScan.ai_analysis.key_findings?.map((finding, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon><InfoIcon color="primary" /></ListItemIcon>
                        <ListItemText primary={finding} />
                      </ListItem>
                    ))}
                  </List>

                  <Typography variant="subtitle2">{t('enrichment.aiAnalysis.recommendations', 'Recommandations')}</Typography>
                  <List dense>
                    {selectedScan.ai_analysis.strategic_recommendations?.map((rec, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                        <ListItemText primary={rec} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => setDetailDialogOpen(false)}
          sx={{ textTransform: 'none' }}
        >
          {t('common.close', 'Fermer')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box className="main-content-with-sidebar" sx={{ p: 3, position: 'relative' }}>
      {/* Free Plan Upgrade Banner - only show after subscription data has loaded */}
      {!subscriptionLoading && !canUseEnrichment && (
        <Box sx={{ mb: 3 }}>
          <UpgradeOverlay feature="data enrichment" compact={true} />
        </Box>
      )}

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>
          <LanguageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('enrichment.title', 'Data enrichment')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('enrichment.description', 'Analyze targets with multiple security intelligence sources')}
        </Typography>
      </Box>

      {/* Alerts - Hide errors during auth redirect and filter auth-related content */}
      {error && !isAuthRedirecting && !isAuthRelatedMessage(error) && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Loading indicator */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Scan form */}
      {renderScanForm()}

      {/* Quick result */}
      {renderQuickResult()}

      {/* Threat summary */}
      {renderThreatSummary()}

      {/* Scans table */}
      {renderScansTable()}

      {/* Detail dialog */}
      {renderDetailDialog()}
    </Box>
  );
};

export default EnrichmentDashboard;
